'use client';

import { t } from 'mobx-state-tree';

export const EditorErrorsStoreModel = t
  .model('EditorErrorsStore', {
    buildErrors: t.array(t.string),
    showBuildErrorOverlay: false,
    playModeErrors: t.array(t.string),
    playModeFatalErrors: t.array(t.string),
    playModeErrorOrder: t.array(t.string),
    showPlayModeErrorOverlay: false,
    suppressNextPlayModeOverlay: false,
    renderErrorName: t.maybeNull(t.string),
    renderError: t.maybeNull(t.string),
    renderErrorStack: t.maybeNull(t.string),
  })
  .views((self) => ({
    get buildErrorCount() { return self.buildErrors.length; },
    get hasBuildErrors() { return self.buildErrors.length > 0; },
    get hasPlayModeErrors() { return self.playModeErrors.length > 0; },
    get hasPlayModeFatalErrors() { return self.playModeFatalErrors.length > 0; },
    get hasAnyPlayModeErrors() { return self.playModeErrorOrder.length > 0; },
    get playModeErrorCount() { return self.playModeErrorOrder.length; },
    get allPlayModeErrors() {
      return self.playModeErrorOrder.slice();
    },
    get hasRenderError() { return self.renderError !== null; },
  }))
  .actions((self) => ({
    setBuildErrors(errors: string[]) {
      self.buildErrors.clear();
      errors.forEach((error) => self.buildErrors.push(error));
    },
    addBuildError(message: string) {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) {
        return;
      }
      if (!self.buildErrors.includes(trimmedMessage)) {
        self.buildErrors.push(trimmedMessage);
      }
    },
    clearBuildErrors() {
      self.buildErrors.clear();
    },
    setShowBuildErrorOverlay(show: boolean) {
      self.showBuildErrorOverlay = show;
    },
    addPlayModeError(message: string) {
      const trimmedMessage = message.trim();
      if (trimmedMessage.length === 0) {
        return;
      }
      if (!self.playModeErrors.includes(trimmedMessage)) {
        self.playModeErrors.push(trimmedMessage);
      }
      if (self.playModeErrorOrder.includes(trimmedMessage)) {
        return;
      }
      if (self.playModeErrorOrder.some(existing => existing.includes(trimmedMessage))) {
        return;
      }
      for (let i = self.playModeErrorOrder.length - 1; i >= 0; i--) {
        if (trimmedMessage.includes(self.playModeErrorOrder[i])) {
          self.playModeErrorOrder.splice(i, 1);
        }
      }
      self.playModeErrorOrder.push(trimmedMessage);
    },
    addPlayModeFatalError(message: string) {
      const trimmedMessage = message.trim();
      if (trimmedMessage.length === 0) {
        return;
      }
      if (!self.playModeFatalErrors.includes(trimmedMessage)) {
        self.playModeFatalErrors.push(trimmedMessage);
      }
    },
    markPlayModeFatal() {
      if (!self.playModeFatalErrors.includes('fatal')) {
        self.playModeFatalErrors.push('fatal');
      }
    },
    clearPlayModeErrors() {
      self.playModeErrors.clear();
      self.playModeFatalErrors.clear();
      self.playModeErrorOrder.clear();
      self.showPlayModeErrorOverlay = false;
      self.suppressNextPlayModeOverlay = false;
    },
    setShowPlayModeErrorOverlay(show: boolean) {
      self.showPlayModeErrorOverlay = show;
    },
    setSuppressNextPlayModeOverlay(suppress: boolean) {
      self.suppressNextPlayModeOverlay = suppress;
    },
    consumeSuppressNextPlayModeOverlay() {
      const suppress = self.suppressNextPlayModeOverlay;
      self.suppressNextPlayModeOverlay = false;
      return suppress;
    },
    setRenderError(name: string | null, message: string | null, stack: string | null) {
      self.renderErrorName = name;
      self.renderError = message;
      self.renderErrorStack = stack;
    },
    clearRenderError() {
      self.renderErrorName = null;
      self.renderError = null;
      self.renderErrorStack = null;
    },
  }));
